import axios from 'axios'
import api from '../api'
import pkg from '../../../package.json'

const DEDUPE_WINDOW_MS = 10000
const MAX_PER_SESSION = 20

// Tracking state (dedupe + cap) lives in closure, NOT in Vuex state:
// it is infrastructure, not data the UI consumes, and a reactive Map in
// Vue 2 is awkward (Vue.set does not work on Map keys).
let seen = new Map()
let sent_count = 0

function _First_Stack_Frame (stack) {
  if (!stack) return ''
  const lines = String(stack).split('\n')
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim()
    if (trimmed.indexOf('at ') === 0) return trimmed
  }
  return ''
}

function _Build_Hash (message, stack, component_name) {
  return (component_name || '') + '|' + message + '|' + _First_Stack_Frame(stack)
}

function _Build_Payload (raw) {
  const err = raw && raw.err
  let message
  let stack = null
  let error_name = null
  if (err instanceof Error) {
    message = err.message || 'Unknown error'
    stack = err.stack || null
    error_name = err.name || null
  } else if (typeof err === 'string') {
    message = err
  } else if (err && typeof err.message === 'string') {
    message = err.message
    stack = err.stack || null
    error_name = (typeof err.name === 'string' && err.name) || null
  } else {
    message = String(err || 'Unknown error')
  }
  return {
    message: message,
    stack: stack,
    route: (raw && raw.route) || null,
    app_version: pkg.version,
    component_name: (raw && raw.component_name) || null,
    // Vue's third arg to errorHandler — tells us WHICH hook crashed
    // ("render", "watcher for X", "beforeCreate hook", etc.). This is
    // the single most useful field for narrowing minified stacks.
    info: (raw && raw.info) || null,
    // Error constructor name — RangeError == stack overflow, TypeError
    // == null/undefined access, etc. Distinguishes classes of failure.
    error_name: error_name,
    // Walk of vm.$parent up to root — locates the crashing component
    // in the tree, not just its name. Empty on window-level errors.
    parent_chain: (raw && raw.parent_chain) || [],
    // Last ~20 events (route changes + Vuex action/mutation names, no
    // payloads — PII-safe) leading up to the crash. Reveals the trigger.
    breadcrumbs: (raw && raw.breadcrumbs) || [],
    // Vue's own warnings preceding the crash. "You may have an infinite
    // update loop..." here == watcher loop; empty == observe recursion.
    vue_warnings: (raw && raw.vue_warnings) || [],
    // Browser identity — Vue 2.7 reactivity bugs sometimes only manifest
    // on Safari (larger stack limit changes recursion behaviour).
    user_agent: (raw && raw.user_agent) || null,
    // Groups all errors from one tab session (sessionStorage-backed).
    session_id: (raw && raw.session_id) || null,
    // Wall-clock timestamp of when the crash was observed on the client.
    timestamp_iso: (raw && raw.timestamp_iso) || null
  }
}

function _Should_Report (hash, now) {
  if (sent_count >= MAX_PER_SESSION) return false
  const last = seen.get(hash)
  if (last != null && now - last < DEDUPE_WINDOW_MS) return false
  return true
}

export const state = {}
export const mutations = {}
export const getters = {}

export const actions = {
  async report_fe_error (_, raw) {
    const payload = _Build_Payload(raw)
    const hash = _Build_Hash(payload.message, payload.stack, payload.component_name)
    const now = Date.now()
    if (!_Should_Report(hash, now)) return

    seen.set(hash, now)
    sent_count++
    try {
      await axios.post(api.log_fe_error, payload)
    } catch (_) {
      // swallow — reporter must never throw
    }
  }
}

export function _Reset_For_Tests () {
  seen = new Map()
  sent_count = 0
}
